import { TimeSlot } from "@/models/Schedule";
import { WorkoutType } from "@/models/WorkoutType";
import { Client } from "@/models/Client";
import { timeSlots as initialTimeSlots, workoutTypes, clients } from "@/services";
import { ClientsSortField, ClientsSortDirection, CLIENTS_PAGINATION_CONFIG } from "@/constants/clientsPagination";

// Local storage keys
const TIMESLOTS_KEY = 'fitness_app_timeslots';
const BOOKINGS_KEY = 'fitness_app_bookings';

// Organization-specific localStorage keys for clients data
const getClientsListStateKey = (organizationId?: string) => 
  organizationId ? `fitness_app_clients_list_state_${organizationId}` : 'fitness_app_clients_list_state';
const getClientsScrollKey = (organizationId?: string) => 
  organizationId ? `fitness_app_clients_scroll_${organizationId}` : 'fitness_app_clients_scroll';

// Define a booking interface
export interface CalendarBooking {
  id: string;
  timeSlotId: string;
  clientId: string;
  date: string; // ISO string format
}

// Get time slots from localStorage or use initial data
export const getTimeSlots = (): TimeSlot[] => {
  const storedTimeSlots = localStorage.getItem(TIMESLOTS_KEY);
  if (storedTimeSlots) {
    return JSON.parse(storedTimeSlots);
  }
  // Save initial time slots to localStorage if not exists
  saveTimeSlots(initialTimeSlots);
  return initialTimeSlots;
};

// Save time slots to localStorage
export const saveTimeSlots = (timeSlots: TimeSlot[]): void => {
  localStorage.setItem(TIMESLOTS_KEY, JSON.stringify(timeSlots));
};

// Get bookings from localStorage or initialize empty array
export const getBookings = (): CalendarBooking[] => {
  const storedBookings = localStorage.getItem(BOOKINGS_KEY);
  if (storedBookings) {
    return JSON.parse(storedBookings);
  }
  return [];
};

// Save bookings to localStorage
export const saveBookings = (bookings: CalendarBooking[]): void => {
  localStorage.setItem(BOOKINGS_KEY, JSON.stringify(bookings));
};

// Add a new booking
export const addBooking = (timeSlotId: string, clientId: string, date: Date): CalendarBooking => {
  const bookings = getBookings();
  
  // Check if booking already exists
  const existingBooking = bookings.find(
    booking => booking.timeSlotId === timeSlotId && 
              booking.clientId === clientId && 
              new Date(booking.date).toDateString() === date.toDateString()
  );
  
  if (existingBooking) {
    return existingBooking;
  }
  
  // Create new booking
  const newBooking: CalendarBooking = {
    id: crypto.randomUUID(),
    timeSlotId,
    clientId,
    date: date.toISOString(),
  };
  
  // Add to bookings array and save
  bookings.push(newBooking);
  saveBookings(bookings);
  
  return newBooking;
};

// Remove a booking
export const removeBooking = (bookingId: string): void => {
  const bookings = getBookings();
  const updatedBookings = bookings.filter(booking => booking.id !== bookingId);
  saveBookings(updatedBookings);
};

// Get all workout types
export const getWorkoutTypes = (): WorkoutType[] => {
  return workoutTypes;
};

// Get all clients
export const getClients = (): Client[] => {
  return clients;
};

// Get bookings for a specific date
export const getBookingsForDate = (date: Date): CalendarBooking[] => {
  const bookings = getBookings();
  return bookings.filter(booking => 
    new Date(booking.date).toDateString() === date.toDateString()
  );
};

// Get bookings for a specific time slot on a specific date
export const getBookingsForTimeSlot = (timeSlotId: string, date: Date): CalendarBooking[] => {
  const bookings = getBookings();
  return bookings.filter(
    booking => booking.timeSlotId === timeSlotId && 
              new Date(booking.date).toDateString() === date.toDateString()
  );
};

// Clients list state management
export interface ClientsListState {
  sortField: ClientsSortField;
  sortDirection: ClientsSortDirection;
  searchTerm: string;
  selectedWorkoutType: string | null;
  showSoonExpire: boolean;
  showUnpaidOnly: boolean;
  showNoSubscriptionsOnly: boolean;
  lastViewedClientId?: string;
  scrollPosition?: number;
  shouldRestoreScroll?: boolean;
}

export const getClientsListState = (organizationId?: string): ClientsListState => {
  const storageKey = getClientsListStateKey(organizationId);
  const stored = localStorage.getItem(storageKey);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // Fallback to default if parsing fails
    }
  }
  
  return {
    sortField: CLIENTS_PAGINATION_CONFIG.DEFAULT_SORT_FIELD,
    sortDirection: CLIENTS_PAGINATION_CONFIG.DEFAULT_SORT_DIRECTION,
    searchTerm: '',
    selectedWorkoutType: null,
    showSoonExpire: false,
    showUnpaidOnly: false,
    showNoSubscriptionsOnly: false, // Show all clients by default
  };
};

export const saveClientsListState = (state: ClientsListState, organizationId?: string): void => {
  const storageKey = getClientsListStateKey(organizationId);
  localStorage.setItem(storageKey, JSON.stringify(state));
};

export const clearClientsListState = (organizationId?: string): void => {
  const stateKey = getClientsListStateKey(organizationId);
  const scrollKey = getClientsScrollKey(organizationId);
  localStorage.removeItem(stateKey);
  localStorage.removeItem(scrollKey);
};

// Reset client list state to default (useful after client creation)
export const resetClientsListStateToDefault = (organizationId?: string): void => {
  const defaultState: ClientsListState = {
    sortField: CLIENTS_PAGINATION_CONFIG.DEFAULT_SORT_FIELD,
    sortDirection: CLIENTS_PAGINATION_CONFIG.DEFAULT_SORT_DIRECTION,
    searchTerm: '',
    selectedWorkoutType: null,
    showSoonExpire: false,
    showUnpaidOnly: false,
    showNoSubscriptionsOnly: false, // Show all clients by default
  };
  saveClientsListState(defaultState, organizationId);
};

export const saveClientsScrollPosition = (position: number, organizationId?: string): void => {
  const storageKey = getClientsScrollKey(organizationId);
  localStorage.setItem(storageKey, position.toString());
};

export const getClientsScrollPosition = (organizationId?: string): number => {
  const storageKey = getClientsScrollKey(organizationId);
  const stored = localStorage.getItem(storageKey);
  return stored ? parseInt(stored, 10) : 0;
};
